import { useEffect, useRef } from 'react';
import emailAutomationService from '../../services/emailAutomationService';
import logger from '../../utils/logger';

/**
 * Background scheduler component for processing pending email automation executions
 * This component runs in the background and processes scheduled automations
 */
const EmailAutomationScheduler = () => {
  const intervalRef = useRef(null);
  const isProcessingRef = useRef(false);

  useEffect(() => {
    // Start the scheduler when component mounts
    startScheduler();

    // Cleanup on unmount
    return () => {
      stopScheduler();
    };
  }, []);

  const startScheduler = () => {
    logger.log('📅 Starting Email Automation Scheduler');
    
    // Process pending executions every 30 seconds
    intervalRef.current = setInterval(async () => {
      if (isProcessingRef.current) {
        logger.log('⏳ Automation scheduler already processing, skipping...');
        return;
      }

      try {
        isProcessingRef.current = true;
        await emailAutomationService.processPendingExecutions();
      } catch (error) {
        logger.error('Error in automation scheduler:', error);
      } finally {
        isProcessingRef.current = false;
      }
    }, 30000); // 30 seconds

    // Also process immediately on start
    setTimeout(async () => {
      try {
        await emailAutomationService.processPendingExecutions();
      } catch (error) {
        logger.error('Error in initial automation processing:', error);
      }
    }, 5000); // Wait 5 seconds after app start
  };

  const stopScheduler = () => {
    if (intervalRef.current) {
      logger.log('🛑 Stopping Email Automation Scheduler');
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // This component doesn't render anything
  return null;
};

export default EmailAutomationScheduler;